import { useState } from "react";
import { Box, Paper, Text } from "@mantine/core";
import { IconEyeOff, IconGitBranch, IconGripVertical } from "@tabler/icons-react";
import { Badge } from "@onengine/component-library";
import { getFieldIcon } from "@/components/features/FormBuilder/data/fieldTypes";
import type { FormQuestion } from "../../types/form.types";
import { EmptyQuestionWarning } from "./EmptyQuestionWarning";
import { isEmptyQuestion } from "../Shared/findEmptyQuestions";
import styles from "./QuestionCard.module.scss";

interface Props {
  question: FormQuestion;
  isHiddenByLogic: boolean;
  hasLogic: boolean;
  metaText: string | null;
  dragHandleProps?: Record<string, unknown>;
  onSelect: () => void;
}

export function CompactQuestionRow({
  question: q,
  isHiddenByLogic,
  hasLogic,
  metaText,
  dragHandleProps,
  onSelect,
}: Props) {
  const Icon = getFieldIcon(q.type);
  const [hover, setHover] = useState(false);
  const isEmpty = isEmptyQuestion(q);

  return (
    <Paper
      data-qcard
      radius="8px"
      withBorder
      flex={1}
      miw={0}
      onClick={onSelect}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        height: 40,
        padding: "0 10px",
        cursor: "pointer",
        background: "var(--color-white)",
        transition: "border-color 0.15s",
        borderColor: hover ? "var(--bg-brand-hover)" : "var(--border-default)",
        borderStyle: isHiddenByLogic || q.hidden ? "dashed" : "solid",
        borderWidth: isHiddenByLogic || q.hidden ? 2 : 1,
      }}
    >
      <Box
        style={{ display: "flex", alignItems: "center", cursor: "grab", flexShrink: 0 }}
        {...dragHandleProps}
      >
        <IconGripVertical size={14} color="#afb8c5" />
      </Box>
      <Icon size={14} color="#646d7e" style={{ flexShrink: 0 }} />
      <Text
        size="sm"
        fw={"var(--font-weight-medium,500)"}
        c={isEmpty ? "var(--text-warning-default)" : "var(--text-default)"}
        style={{
          flexShrink: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {isEmpty ? `${q.typeName} (no question yet)` : q.question}
      </Text>
      {isEmpty && <EmptyQuestionWarning />}
      {q.required && (
        <Text span c="red" ml={-4}>
          *
        </Text>
      )}
      <Box style={{ flex: 1 }} />
      {hasLogic && (
        <Box style={{ flexShrink: 0 }}>
          <Badge
            color="info"
            variant="outline"
            size="medium"
            iconLeft={<IconGitBranch size={16} />}
            className={styles.logicAppliedClass}
          >
            Logic Applied
          </Badge>
        </Box>
      )}
      {isHiddenByLogic && (
        <Box style={{ flexShrink: 0 }}>
          <Badge
            color="neutral"
            variant="lowContrast"
            size="medium"
            iconLeft={<IconEyeOff size={16} />}
            className={styles.logichiddenClass}
          >
            Hidden by Logic
          </Badge>
        </Box>
      )}
      <Text
        size="sm"
        c="dimmed"
        style={{
          whiteSpace: "nowrap",
          flexShrink: 1,
          minWidth: 0,
          maxWidth: "40%",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {metaText || q.typeName}
      </Text>
    </Paper>
  );
}
